import { Fragment, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { History, PackagePlus, Layers, Building2, Printer, Download, ChevronRight, ChevronDown, CalendarClock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BatchPanel, expStatus, fmtDate } from "./stock/BatchPanel";

const FILTERS = [
  { v: "all", l: "All products" },
  { v: "soon", l: "Expiring soon" },
  { v: "expired", l: "Expired" },
  { v: "missing", l: "Dates missing" },
  { v: "refill", l: "Needs refill" },
];

const matchFilter = (p, f, expDays) => {
  if (f === "all") return true;
  if (f === "missing") return p.undated_units > 0;
  if (f === "refill") return p.packs_to_fill > 0 || p.below_par;
  return p.batches.some((b) => expStatus(b.best_before, expDays)?.key === f);
};

const batchSummary = (p) => p.batches.map((b) => `${b.qty} × ${fmtDate(b.best_before)}${b.kiosk_name ? ` (${b.kiosk_name})` : ""}`).join("; ");

export default function Stock() {
  const [products, setProducts] = useState([]);
  const [audit, setAudit] = useState([]);
  const [totals, setTotals] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [buildingId, setBuildingId] = useState("");
  const [kioskId, setKioskId] = useState("all");
  const [plan, setPlan] = useState(null);
  const [parDraft, setParDraft] = useState({});
  const [expDays, setExpDays] = useState(7);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState({});
  const [adjust, setAdjust] = useState(null);
  const [adjForm, setAdjForm] = useState({ cases: 0, loose: 0, par: 0, reason: "adjustment", best_before: "" });
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignForm, setAssignForm] = useState({ kiosk_id: "", product_id: "", cases: 0, loose: 0, par: 12, price_override: "", best_before: "" });

  const kid = kioskId === "all" ? null : kioskId;
  const kiosks = plan?.kiosks || [];

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data.filter((p) => !p.archived)));
    api.get("/building-list").then((r) => { setBuildings(r.data); if (r.data.length) setBuildingId(r.data[0].id); }).catch(() => {});
  }, []);

  const load = () => {
    if (!buildingId) return;
    api.get(`/buildings/${buildingId}/stock-plan${kid ? `?kiosk_id=${kid}` : ""}`).then((r) => {
      setPlan(r.data); setParDraft({});
      if (typeof r.data.expiring_soon_days === "number") setExpDays(r.data.expiring_soon_days);
    }).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
    api.get(`/stock/audit?${kid ? `kiosk_id=${kid}` : `building_id=${buildingId}`}`).then((r) => setAudit(r.data)).catch(() => {});
    api.get("/stock/totals").then((r) => setTotals(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [buildingId, kioskId]); // eslint-disable-line

  const changeBuilding = (v) => { setKioskId("all"); setPlan(null); setOpen({}); setBuildingId(v); };

  const savePar = async (pid, val) => {
    try { await api.put(`/buildings/${buildingId}/pars`, { product_id: pid, par_level: Number(val) || 0 }); toast.success("Building PAR saved"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const saveExpDays = async (val) => {
    const d = Math.max(1, Number(val) || 7);
    try { await api.put("/settings/stock", { expiring_soon_days: d }); setExpDays(d); toast.success("Expiry warning window saved"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const rows = (plan?.products || []).filter((p) => matchFilter(p, filter, expDays));
  const pickingRows = () => (plan?.products || []).filter((p) => p.packs_to_fill > 0);
  const scopeName = () => `${plan?.building_name || ""}${kid ? ` · ${kiosks.find((k) => k.id === kid)?.name || ""}` : ""}`;
  const csvCell = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };

  const exportCsv = () => {
    const list = plan?.products || [];
    if (!list.length) { toast.info("No products to export."); return; }
    const header = ["Product", "SKU", "Units per pack", "Fully stocked", "Current", "Deficit units", "Packs to fill", "Short-dated", "Dates missing (units)", "Batches"];
    const body = list.map((p) => [p.name, p.sku || "", p.pack_size, p.fully_stocked_units, p.current_units, p.deficit_units, p.packs_to_fill, p.short_dated || "", p.undated_units, batchSummary(p)]);
    const csv = [header, ...body].map((r) => r.map(csvCell).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `stock-${scopeName().replace(/[^a-z0-9]+/gi, "-")}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };

  const printList = () => {
    const list = pickingRows();
    if (!list.length) { toast.info("Nothing to refill — everything is fully stocked."); return; }
    const w = window.open("", "_blank");
    if (!w) { toast.error("Please allow pop-ups to print."); return; }
    const tr = list.map((p) => `<tr><td>${p.name}</td><td>${p.sku || ""}</td><td style="text-align:right;font-weight:700">${p.packs_to_fill}</td><td style="text-align:right">${p.pack_size}</td><td style="text-align:right">${p.deficit_units}</td><td>${p.short_dated ? fmtDate(p.short_dated) : "—"}</td><td style="width:60px"></td></tr>`).join("");
    w.document.write(`<html><head><title>Picking list</title><style>body{font-family:system-ui,-apple-system,sans-serif;padding:24px;color:#111}h1{font-size:18px;margin:0 0 4px}p{color:#666;margin:0 0 16px;font-size:12px}table{width:100%;border-collapse:collapse;font-size:13px}th,td{border-bottom:1px solid #ddd;padding:8px;text-align:left}th{background:#f5f5f5}</style></head><body><h1>Restock picking list — ${scopeName()}</h1><p>Generated ${new Date().toLocaleString("en-GB")} · ${list.length} item(s) to refill</p><table><thead><tr><th>Product</th><th>SKU</th><th style="text-align:right">Packs to pick</th><th style="text-align:right">Units/pack</th><th style="text-align:right">Deficit units</th><th>Short-dated on shelf</th><th>Picked</th></tr></thead><tbody>${tr}</tbody></table></body></html>`);
    w.document.close(); w.focus();
    setTimeout(() => w.print(), 150);
  };

  const openAdjust = (p) => {
    const ps = p.pack_size || 1;
    setAdjust(p);
    setAdjForm({ cases: Math.floor(p.current_units / ps), loose: p.current_units % ps, par: p.par ?? 0, reason: "adjustment", best_before: "" });
  };
  const adjustPack = adjust?.pack_size || 1;
  const adjTotal = Number(adjForm.cases || 0) * adjustPack + Number(adjForm.loose || 0);
  const saveAdjust = async () => {
    try {
      await api.post("/stock/adjust", { kiosk_id: kid, product_id: adjust.product_id, new_qty: adjTotal, reason: adjForm.reason, par_level: Number(adjForm.par) || 0, best_before: adjForm.best_before || null });
      toast.success("Stock updated"); setAdjust(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openAssign = () => { setAssignForm({ kiosk_id: kid || (kiosks.length === 1 ? kiosks[0].id : ""), product_id: "", cases: 0, loose: 0, par: 12, price_override: "", best_before: "" }); setAssignOpen(true); };
  const assignPack = products.find((p) => p.id === assignForm.product_id)?.pack_size || 1;
  const assignTotal = Number(assignForm.cases || 0) * assignPack + Number(assignForm.loose || 0);
  const saveAssign = async () => {
    try {
      await api.post("/kiosk-products", {
        kiosk_id: assignForm.kiosk_id, product_id: assignForm.product_id, stock_qty: assignTotal,
        par_level: Number(assignForm.par) || 0, low_stock_threshold: Number(assignForm.par) || 5,
        price_override: assignForm.price_override ? Number(assignForm.price_override) : null, active: true,
        best_before: assignForm.best_before || null,
      });
      toast.success("Product assigned"); setAssignOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const [assignedIds, setAssignedIds] = useState([]);
  useEffect(() => {
    if (!assignOpen || !assignForm.kiosk_id) { setAssignedIds([]); return; }
    api.get(`/kiosk-products?kiosk_id=${assignForm.kiosk_id}`).then((r) => setAssignedIds(r.data.map((x) => x.product_id))).catch(() => {});
  }, [assignOpen, assignForm.kiosk_id]);
  const assignable = products.filter((p) => !assignedIds.includes(p.id));

  const statusBadge = (p) => p.below_par ? <Badge variant="destructive">Below PAR</Badge>
    : p.packs_to_fill > 0 ? <Badge variant="outline" className="text-amber-600 border-amber-200">Refill</Badge>
    : <Badge variant="outline" className="text-emerald-600 border-emerald-200">Full</Badge>;

  const bestBeforeCell = (p, tid) => {
    const s = expStatus(p.short_dated, expDays);
    return (
      <div className="flex items-center gap-1.5 flex-wrap">
        {p.short_dated ? <span className="text-sm" data-testid={`stock-short-dated-${tid}`}>{fmtDate(p.short_dated)}</span> : <span className="text-sm text-slate-400">—</span>}
        {s && s.key !== "ok" && <Badge variant="outline" className={s.cls} data-testid={`stock-exp-badge-${tid}`}>{s.label}</Badge>}
        {p.batches.length > 1 && <Badge variant="secondary" className="text-xs" data-testid={`stock-more-batches-${tid}`}>+{p.batches.length - 1} more</Badge>}
        {p.undated_units > 0 && <Badge variant="outline" className="text-xs text-amber-600 border-amber-200" data-testid={`stock-missing-${tid}`}>{p.undated_units} undated</Badge>}
      </div>
    );
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 min-w-0">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-admin-head text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white">Stock</h1>
          <p className="text-slate-500 mt-1">Counts, PAR, refills and best-before batches — by building or kiosk</p>
        </div>
        <Button data-testid="stock-assign-button" onClick={openAssign} disabled={!kiosks.length} className="bg-blue-600 hover:bg-blue-700"><PackagePlus className="h-4 w-4 mr-1.5" /> Assign product</Button>
      </div>

      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
            <Building2 className="h-4 w-4 text-blue-600 hidden sm:block" />
            <Select value={buildingId} onValueChange={changeBuilding}>
              <SelectTrigger data-testid="stock-building-select" className="w-full sm:w-56"><SelectValue placeholder="Select building" /></SelectTrigger>
              <SelectContent>{buildings.map((b) => <SelectItem key={b.id} value={b.id}>{b.group ? `${b.group} · ` : ""}{b.name}</SelectItem>)}</SelectContent>
            </Select>
            <ChevronRight className="h-4 w-4 text-slate-300 hidden sm:block" />
            <Select value={kioskId} onValueChange={(v) => { setOpen({}); setKioskId(v); }}>
              <SelectTrigger data-testid="stock-kiosk-select" className="w-full sm:w-56"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All kiosks in building ({kiosks.length})</SelectItem>
                {kiosks.map((k) => <SelectItem key={k.id} value={k.id}>{k.name} ({k.code})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger data-testid="stock-filter" className="w-40 h-9"><SelectValue /></SelectTrigger>
              <SelectContent>{FILTERS.map((f) => <SelectItem key={f.v} value={f.v}>{f.l}</SelectItem>)}</SelectContent>
            </Select>
            <Button data-testid="picking-print" variant="outline" size="sm" onClick={printList}><Printer className="h-4 w-4 mr-1.5" /> Print</Button>
            <Button data-testid="picking-csv" variant="outline" size="sm" onClick={exportCsv}><Download className="h-4 w-4 mr-1.5" /> CSV</Button>
            <div className="flex items-center gap-1.5 pl-1">
              <span className="text-xs text-slate-400">Expiring soon within</span>
              <Input type="number" min={1} data-testid="stock-exp-days" className="h-8 w-16 text-center" value={expDays}
                onChange={(e) => setExpDays(parseInt(e.target.value || "0", 10) || 0)} onBlur={(e) => saveExpDays(e.target.value)} />
              <span className="text-xs text-slate-400">days</span>
            </div>
          </div>
        </div>
        <p className="px-4 pt-3 text-xs text-slate-400 flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5" /> Open a product to manage its best-before batches. Sales and reductions take units from the shortest-dated batch first.{!kid && " Pick a kiosk to adjust counts."}</p>
        <div className="md:hidden divide-y divide-slate-100 dark:divide-slate-800 mt-2" data-testid="stock-mobile-list">
          {rows.map((p) => {
            const tid = p.sku || p.product_id;
            const isOpen = !!open[p.product_id];
            return (
              <div key={p.product_id} className="px-4 py-3 space-y-2.5" data-testid={`stock-card-${tid}`}>
                <button type="button" className="w-full flex items-start gap-2 text-left" onClick={() => setOpen({ ...open, [p.product_id]: !isOpen })} data-testid={`stock-card-toggle-${tid}`}>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-slate-900 dark:text-white">{p.name}</p>
                    <p className="text-xs text-slate-400">{p.sku || "—"}{p.pack_size > 1 ? ` · ${p.pack_size}/pack` : ""}</p>
                  </div>
                  <span className="shrink-0">{statusBadge(p)}</span>
                  {isOpen ? <ChevronDown className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" /> : <ChevronRight className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" />}
                </button>
                <div className="grid grid-cols-4 gap-2 text-center">
                  {[["Full", p.fully_stocked_units], ["Current", p.current_units], ["Packs", p.packs_to_fill], [kid ? "PAR" : "Bldg PAR", null]].map(([l, v]) => (
                    <div key={l} className="rounded-lg bg-slate-50 dark:bg-slate-900 py-1.5">
                      <p className="text-[10px] uppercase tracking-wide text-slate-400">{l}</p>
                      {v !== null ? <p className={`text-sm font-semibold ${l === "Packs" && v > 0 ? "text-rose-600" : "text-slate-900 dark:text-white"}`} data-testid={`stock-card-${l.toLowerCase()}-${tid}`}>{v}</p> : kid ? (
                        <p className="text-sm font-semibold" data-testid={`stock-card-par-${tid}`}>{p.par ?? "—"}</p>
                      ) : (
                        <input type="number" min={0} inputMode="numeric" placeholder="—" data-testid={`stock-card-par-input-${tid}`}
                          className="w-full bg-transparent text-center text-sm font-semibold outline-none"
                          value={parDraft[p.product_id] ?? (p.par_building ?? "")}
                          onChange={(e) => setParDraft({ ...parDraft, [p.product_id]: e.target.value })}
                          onBlur={(e) => { const v = e.target.value; if (v !== "" && Number(v) !== (p.par_building ?? null)) savePar(p.product_id, v); }} />
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">{bestBeforeCell(p, `m-${tid}`)}</div>
                  {kid && p.assigned && <Button data-testid={`stock-card-adjust-${tid}`} variant="outline" size="sm" className="shrink-0" onClick={() => openAdjust(p)}>Adjust</Button>}
                </div>
                {isOpen && <div className="rounded-lg bg-slate-50/70 dark:bg-slate-900/40 px-2"><BatchPanel row={p} kiosks={kiosks} kioskId={kid} expDays={expDays} onChanged={load} /></div>}
              </div>
            );
          })}
          {!rows.length && <p className="text-center text-slate-400 py-8 text-sm">{!plan ? "Loading…" : filter !== "all" ? "Nothing matches this filter." : "No products here yet."}</p>}
        </div>
        <div className="hidden md:block overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8" />
                <TableHead>Product</TableHead>
                <TableHead className="text-right">Fully stocked</TableHead>
                <TableHead className="text-right">Current</TableHead>
                <TableHead className="text-right">Packs to fill</TableHead>
                <TableHead className="text-right">{kid ? "Kiosk PAR" : "Building PAR"}</TableHead>
                <TableHead>Short-dated</TableHead>
                <TableHead>Status</TableHead>
                {kid && <TableHead className="text-right">Action</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((p) => {
                const tid = p.sku || p.product_id;
                const isOpen = !!open[p.product_id];
                return (
                  <Fragment key={p.product_id}>
                    <TableRow data-testid={`stock-plan-row-${tid}`} className="cursor-pointer" onClick={() => setOpen({ ...open, [p.product_id]: !isOpen })}>
                      <TableCell className="pr-0">{isOpen ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}</TableCell>
                      <TableCell className="font-medium text-slate-900 dark:text-white" data-testid={`stock-row-toggle-${tid}`}>{p.name}<span className="text-xs text-slate-400 ml-1">{p.pack_size > 1 ? `· ${p.pack_size}/pack` : ""}</span></TableCell>
                      <TableCell className="text-right">{p.fully_stocked_units}</TableCell>
                      <TableCell className="text-right" data-testid={`stock-current-${tid}`}>{p.current_units}</TableCell>
                      <TableCell className="text-right"><Badge variant={p.packs_to_fill > 0 ? "destructive" : "secondary"} data-testid={`building-plan-packs-${tid}`}>{p.packs_to_fill}</Badge></TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        {kid ? <span className="text-sm" data-testid={`kiosk-par-${tid}`}>{p.par ?? "—"}</span> : (
                          <Input data-testid={`building-par-input-${tid}`} type="number" min={0} className="h-8 w-20 ml-auto text-right" placeholder="—"
                            value={parDraft[p.product_id] ?? (p.par_building ?? "")}
                            onChange={(e) => setParDraft({ ...parDraft, [p.product_id]: e.target.value })}
                            onBlur={(e) => { const v = e.target.value; if (v !== "" && Number(v) !== (p.par_building ?? null)) savePar(p.product_id, v); }} />
                        )}
                      </TableCell>
                      <TableCell>{bestBeforeCell(p, tid)}</TableCell>
                      <TableCell>{statusBadge(p)}</TableCell>
                      {kid && (
                        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                          {p.assigned ? <Button data-testid={`stock-adjust-${tid}`} variant="outline" size="sm" onClick={() => openAdjust(p)}>Adjust</Button> : <span className="text-xs text-slate-400">Not assigned</span>}
                        </TableCell>
                      )}
                    </TableRow>
                    {isOpen && (
                      <TableRow className="bg-slate-50/60 dark:bg-slate-900/40 hover:bg-slate-50/60">
                        <TableCell />
                        <TableCell colSpan={kid ? 8 : 7}>
                          <BatchPanel row={p} kiosks={kiosks} kioskId={kid} expDays={expDays} onChanged={load} />
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
              {!rows.length && <TableRow><TableCell colSpan={9} className="text-center text-slate-400 py-8">{!plan ? "Loading…" : filter !== "all" ? "Nothing matches this filter." : "No products here yet — build a planogram or assign a product."}</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800 flex items-center gap-2"><Layers className="h-4 w-4 text-blue-600" /><span className="font-semibold text-slate-900 dark:text-white">Total available across all locations</span></div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Product</TableHead><TableHead>SKU</TableHead><TableHead className="text-right">Total units</TableHead><TableHead>Cases + loose</TableHead><TableHead className="text-right">Locations</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {totals.map((t) => (
              <TableRow key={t.product_id} data-testid={`stock-total-${t.sku}`}>
                <TableCell className="font-medium text-slate-900 dark:text-white">{t.name}</TableCell>
                <TableCell className="font-mono-x text-xs">{t.sku}</TableCell>
                <TableCell className="text-right font-semibold">{t.total_units}</TableCell>
                <TableCell className="text-sm text-slate-500">{t.pack_size > 1 ? `${t.cases} case${t.cases === 1 ? "" : "s"}${t.loose ? ` + ${t.loose} loose` : ""}` : "—"}</TableCell>
                <TableCell className="text-right text-sm text-slate-500">{t.locations}</TableCell>
              </TableRow>
            ))}
            {!totals.length && <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8">No stock recorded yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800 flex items-center gap-2"><History className="h-4 w-4 text-blue-600" /><span className="font-semibold text-slate-900 dark:text-white">Audit trail · {scopeName()}</span></div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>When</TableHead><TableHead>Product</TableHead><TableHead className="text-right">Prev</TableHead><TableHead className="text-right">New</TableHead><TableHead className="text-right">Change</TableHead><TableHead>Reason</TableHead><TableHead>By</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {audit.map((a) => (
              <TableRow key={a.id} data-testid={`audit-row-${a.id}`}>
                <TableCell className="text-xs text-slate-500">{a.created_at?.slice(0, 19).replace("T", " ")}</TableCell>
                <TableCell>{a.product_name}</TableCell>
                <TableCell className="text-right">{a.previous_qty}</TableCell>
                <TableCell className="text-right">{a.new_qty}</TableCell>
                <TableCell className={`text-right font-semibold ${a.change < 0 ? "text-red-600" : "text-emerald-600"}`}>{a.change > 0 ? "+" : ""}{a.change}</TableCell>
                <TableCell><Badge variant="outline">{a.reason}</Badge></TableCell>
                <TableCell className="text-xs text-slate-500">{a.user_email}</TableCell>
              </TableRow>
            ))}
            {!audit.length && <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">No adjustments recorded.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!adjust} onOpenChange={(o) => !o && setAdjust(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Adjust stock · {adjust?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">Units per case: <b>{adjustPack}</b>. Enter whole cases held plus any loose units from an opened case.</p>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Full cases</Label><Input data-testid="stock-adjust-cases" type="number" min={0} value={adjForm.cases} onChange={(e) => setAdjForm({ ...adjForm, cases: e.target.value })} className="mt-1" /></div>
              <div><Label>Loose units</Label><Input data-testid="stock-adjust-loose" type="number" min={0} value={adjForm.loose} onChange={(e) => setAdjForm({ ...adjForm, loose: e.target.value })} className="mt-1" /></div>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-300" data-testid="stock-adjust-total">Total sellable units: <b>{adjTotal}</b> {adjust && adjTotal !== adjust.current_units && <span className={adjTotal > adjust.current_units ? "text-emerald-600" : "text-rose-600"}>({adjTotal > adjust.current_units ? "+" : ""}{adjTotal - adjust.current_units})</span>}</p>
            {adjust && adjTotal > adjust.current_units && (
              <div><Label>Best before for the {adjTotal - adjust.current_units} added unit(s) (optional)</Label><Input data-testid="stock-adjust-best-before" type="date" value={adjForm.best_before} onChange={(e) => setAdjForm({ ...adjForm, best_before: e.target.value })} className="mt-1" /></div>
            )}
            {adjust && adjTotal < adjust.current_units && <p className="text-xs text-slate-500">The {adjust.current_units - adjTotal} removed unit(s) will come off the shortest-dated batch first.</p>}
            <div><Label>PAR level — replenish below (units)</Label><Input data-testid="stock-adjust-par" type="number" min={0} value={adjForm.par} onChange={(e) => setAdjForm({ ...adjForm, par: e.target.value })} className="mt-1" /></div>
            <div>
              <Label>Reason</Label>
              <Select value={adjForm.reason} onValueChange={(v) => setAdjForm({ ...adjForm, reason: v })}>
                <SelectTrigger data-testid="stock-adjust-reason" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="adjustment">Manual adjustment</SelectItem>
                  <SelectItem value="replenishment">Replenishment</SelectItem>
                  <SelectItem value="wastage">Wastage</SelectItem>
                  <SelectItem value="stocktake">Stocktake correction</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjust(null)}>Cancel</Button>
            <Button data-testid="stock-adjust-save" onClick={saveAdjust} className="bg-blue-600 hover:bg-blue-700">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Assign product to kiosk</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Kiosk</Label>
              <Select value={assignForm.kiosk_id} onValueChange={(v) => setAssignForm({ ...assignForm, kiosk_id: v })}>
                <SelectTrigger data-testid="stock-assign-kiosk" className="mt-1"><SelectValue placeholder="Select kiosk" /></SelectTrigger>
                <SelectContent>{kiosks.map((k) => <SelectItem key={k.id} value={k.id}>{k.name} ({k.code})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Product</Label>
              <Select value={assignForm.product_id} onValueChange={(v) => setAssignForm({ ...assignForm, product_id: v })}>
                <SelectTrigger data-testid="stock-assign-product" className="mt-1"><SelectValue placeholder="Select product" /></SelectTrigger>
                <SelectContent>{assignable.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} ({p.sku})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <p className="text-xs text-slate-400 -mt-1">Units per case: <b>{assignPack}</b> {assignPack > 1 ? "(set on the product)" : "(sold as single units)"}</p>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Full cases in stock</Label><Input data-testid="stock-assign-cases" type="number" min={0} value={assignForm.cases} onChange={(e) => setAssignForm({ ...assignForm, cases: e.target.value })} className="mt-1" /></div>
              <div><Label>Loose units (opened case)</Label><Input data-testid="stock-assign-loose" type="number" min={0} value={assignForm.loose} onChange={(e) => setAssignForm({ ...assignForm, loose: e.target.value })} className="mt-1" /></div>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-300" data-testid="stock-assign-total">Total sellable units: <b>{assignTotal}</b></p>
            {assignTotal > 0 && <div><Label>Best before (optional)</Label><Input data-testid="stock-assign-best-before" type="date" value={assignForm.best_before} onChange={(e) => setAssignForm({ ...assignForm, best_before: e.target.value })} className="mt-1" /></div>}
            <div><Label>PAR level — replenish below (units)</Label><Input data-testid="stock-assign-par" type="number" min={0} value={assignForm.par} onChange={(e) => setAssignForm({ ...assignForm, par: e.target.value })} className="mt-1" /></div>
            <div><Label>Price override incl VAT (optional £)</Label><Input type="number" step="0.01" value={assignForm.price_override} onChange={(e) => setAssignForm({ ...assignForm, price_override: e.target.value })} placeholder="Leave blank for default" className="mt-1" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignOpen(false)}>Cancel</Button>
            <Button data-testid="stock-assign-save" onClick={saveAssign} disabled={!assignForm.product_id || !assignForm.kiosk_id} className="bg-blue-600 hover:bg-blue-700">Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
